import React from "react";
import classNames from "classnames";
import { useParams } from "react-router-dom";

import Api from "@/api";
import * as Spaces from "@/models/spaces";

import { usePaths } from "@/routes/paths";
import { useCurrentCompany } from "@/contexts/CurrentCompanyContext";
import { PrivacyIndicator } from "@/features/spaces/PrivacyIndicator";

import { DivLink, IconPlus, IconUsers } from "turboui";

//
// The quick-switch Spaces rail.
//
// Shared scaffolding rendered by SpacesRailLayout on the Home and single-Space
// pages, so moving between spaces stays one click. It fetches its own spaces
// list (decoupled from the page route loaders) so it behaves identically no
// matter which page it is mounted on.
//

// Deterministic pastel palette for the per-space initial tiles. `space.color`
// is free-form and not guaranteed to map to a usable token, so we derive a
// stable colour from the space identity instead of trusting it for styling.
const TILE_PALETTE = [
  "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
  "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300",
  "bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300",
  "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300",
  "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
];

function tileClasses(space: Spaces.Space): string {
  const key = space.id || space.name || "";
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  }
  return TILE_PALETTE[hash % TILE_PALETTE.length]!;
}

function initials(name?: string | null): string {
  const trimmed = (name || "").trim();
  if (!trimmed) return "?";

  const first = trimmed[0]!.toUpperCase();
  const second = trimmed.length > 1 ? trimmed[1]!.toLowerCase() : "";
  return first + second;
}

export function SpacesSidebar() {
  const paths = usePaths();
  const company = useCurrentCompany();
  const { id: activeSpaceId } = useParams();

  const { data, loading } = Api.spaces.useList({ includeAccessLevels: true });
  const spaces = data?.spaces ?? [];

  const sorted = React.useMemo(() => {
    return [...spaces].sort((a, b) => {
      if (a.isCompanySpace) return -1;
      if (b.isCompanySpace) return 1;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [spaces]);

  // Permissions may not be hydrated on the context company; only hide an action
  // when we are explicitly told it is not allowed.
  const canCreateSpace = company?.permissions?.canCreateSpace !== false;
  const canInvite = company?.permissions?.canInviteMembers !== false;

  return (
    <div className="flex flex-col text-sm" data-test-id="spaces-sidebar">
      <div className="flex items-center justify-between px-3 pt-4 pb-2">
        <div className="text-xs font-bold tracking-wide uppercase text-content-subtle">Spaces</div>
        {canCreateSpace && (
          <DivLink
            to={paths.newSpacePath()}
            className="w-6 h-6 rounded-md bg-brand-1 text-white-1 flex items-center justify-center hover:opacity-90 transition-opacity"
            testId="sidebar-add-space"
          >
            <IconPlus size={15} />
          </DivLink>
        )}
      </div>

      <div className="px-2 pb-2">
        {loading && spaces.length === 0 ? (
          <SidebarSkeleton />
        ) : (
          <div className="flex flex-col gap-0.5">
            {sorted.map((space) => (
              <SpaceRow
                key={space.id}
                space={space}
                active={space.id === activeSpaceId}
                to={paths.spacePath(space.id!)}
              />
            ))}
          </div>
        )}
      </div>

      {canInvite && (
        <div className="px-2 py-2 mt-1 border-t border-stroke-base">
          <DivLink
            to={paths.invitePeoplePath()}
            className="flex items-center gap-2 px-2 py-2 rounded-md text-content-dimmed hover:bg-surface-dimmed hover:text-content-base transition-colors"
            testId="sidebar-invite-people"
          >
            <IconUsers size={16} />
            <span className="font-medium">Invite People</span>
          </DivLink>
        </div>
      )}
    </div>
  );
}

function SpaceRow({ space, active, to }: { space: Spaces.Space; active: boolean; to: string }) {
  const rowClass = classNames(
    "group flex items-center gap-2.5 px-2 py-1.5 rounded-md cursor-pointer transition-colors",
    {
      "bg-blue-50 dark:bg-blue-950/40 font-semibold text-content-base shadow-[inset_2px_0_0_var(--color-brand-1)]":
        active,
      "text-content-dimmed hover:bg-surface-dimmed hover:text-content-base": !active,
    },
  );

  return (
    <DivLink to={to} className={rowClass} testId={`sidebar-space-${space.id}`}>
      <div
        className={classNames(
          "w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-bold shrink-0",
          tileClasses(space),
        )}
      >
        {initials(space.name)}
      </div>
      <span className="flex-1 truncate">{space.name}</span>
      {space.accessLevels && <PrivacyIndicator space={space} size={13} />}
    </DivLink>
  );
}

function SidebarSkeleton() {
  return (
    <div className="flex flex-col gap-1 px-1 py-1 animate-pulse" data-test-id="spaces-sidebar-skeleton">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex items-center gap-2.5 px-1 py-1.5">
          <div className="w-6 h-6 rounded-md bg-surface-dimmed" />
          <div className="h-3 bg-surface-dimmed rounded flex-1" />
        </div>
      ))}
    </div>
  );
}
